(() => {
  if (window.fmrAdventPairReady) return;
  window.fmrAdventPairReady = true;

  const states = new WeakMap();
  const bypassForms = new WeakSet();
  let activeOffer = null;

  function isUpsellEnabled(card) {
    // A/B scripts may set this boolean before or after this asset loads. Unset uses the theme setting.
    return typeof window.fmrAdventUpsellEnabled === 'boolean'
      ? window.fmrAdventUpsellEnabled
      : card.dataset.atcUpsell === 'true';
  }

  function getState(card) {
    if (!states.has(card)) states.set(card, { busy: false });
    return states.get(card);
  }

  function getContext(card) {
    const component = card.closest('.product-details')?.querySelector('product-form-component');
    const form = component?.querySelector('form');
    const variantId = Number(component?.querySelector('input[name="id"]')?.value);
    const bundleId = Number(card.dataset.bundleVariant);
    const quantityField = component?.querySelector('input[name="quantity"]');
    const quantity = Number(quantityField?.value ?? component?.dataset.quantityDefault ?? 1);
    const selector = component?.querySelector('quantity-selector-component');
    const addButton = component?.querySelector('add-to-cart-component button');

    if (!form || !addButton || addButton.disabled || !form.checkValidity() ||
        variantId !== Number(card.dataset.adventVariant) ||
        !Number.isSafeInteger(variantId) || variantId <= 0 ||
        !Number.isSafeInteger(bundleId) || bundleId <= 0 ||
        !Number.isSafeInteger(quantity) || quantity < 1 ||
        (selector?.canAddToCart && !selector.canAddToCart().canAdd) ||
        !window.FmrCart?.addMultiple) return null;

    return { card, form, addButton, variantId, bundleId, quantity };
  }

  function showError(card, message) {
    const error = card.querySelector('[data-fmr-advent-pair-error]');
    error.textContent = message;
    error.hidden = !message;
  }

  function updateAvailability(card, availability) {
    card.dataset.offerAvailable = String(availability.available);
    card.querySelectorAll('[data-fmr-offer-saving]').forEach((badge) => {
      badge.textContent = availability.available ? badge.dataset.savingLabel : availability.label;
    });
    card.querySelectorAll('[data-fmr-offer-price]').forEach((price) => {
      price.hidden = !availability.available;
    });
    const button = card.querySelector('[data-fmr-advent-pair-add]');
    button.disabled = !availability.available || getState(card).busy;
    button.textContent = availability.available ? button.dataset.buttonLabel : availability.label;
    card.querySelector('[data-fmr-offer-note]').textContent = availability.available
      ? 'Includes one brush set'
      : 'You can still order the Advent Calendar.';
    const accept = card.querySelector('[data-fmr-upsell-accept]');
    if (accept) accept.disabled = !availability.available || getState(card).busy;
  }

  async function variantAvailable(productUrl, variantId, signal) {
    const url = new URL(productUrl, window.location.origin);
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    url.search = '';
    url.hash = '';
    const response = await fetch(url, { cache: 'no-store', signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Availability unavailable');
    const product = await response.json();
    return product.variants.some((variant) => Number(variant.id) === Number(variantId) && variant.available);
  }

  async function checkAvailability(card) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    let availability;
    try {
      const [brushAvailable, bundleAvailable] = await Promise.all([
        variantAvailable(card.dataset.companionUrl, card.dataset.companionVariant, controller.signal),
        variantAvailable(card.dataset.bundleUrl, card.dataset.bundleVariant, controller.signal),
      ]);
      availability = {
        available: brushAvailable && bundleAvailable,
        label: brushAvailable ? 'Set unavailable' : 'Brush set sold out',
      };
    } catch {
      // The regular calendar form remains usable if the offer cannot be verified.
      availability = { available: false, label: 'Offer unavailable' };
    } finally {
      clearTimeout(timeout);
    }
    updateAvailability(card, availability);
    return availability;
  }

  function closeOffer(offer) {
    if (activeOffer === offer) activeOffer = null;
    offer.dialog.close();
    document.documentElement.classList.remove('fmr-advent-upsell-open');
  }

  function continueCalendar(offer) {
    // Re-enter the theme's original click/submit handlers exactly once, including sticky ATC.
    bypassForms.add(offer.form);
    try {
      if (offer.trigger?.isConnected) offer.trigger.click();
      else if (offer.submitter?.isConnected) offer.form.requestSubmit(offer.submitter);
      else offer.form.requestSubmit();
    } finally {
      bypassForms.delete(offer.form);
    }
  }

  async function openOffer(context, trigger, submitter) {
    const dialog = context.card.querySelector('[data-fmr-advent-upsell]');
    const offer = { ...context, dialog, trigger, submitter, adding: false, checking: true };
    activeOffer = offer;
    showError(context.card, '');
    dialog.querySelector('[data-fmr-upsell-accept]').disabled = true;
    dialog.querySelector('[data-fmr-upsell-status]').textContent = 'Checking availability…';
    dialog.showModal();
    document.documentElement.classList.add('fmr-advent-upsell-open');

    const availability = await checkAvailability(context.card);
    if (activeOffer !== offer) return;
    offer.checking = false;
    if (!availability.available) {
      closeOffer(offer);
      continueCalendar(offer);
      return;
    }
    dialog.querySelector('[data-fmr-upsell-accept]').disabled = false;
    dialog.querySelector('[data-fmr-upsell-status]').textContent = '';
  }

  function intercept(event, component, trigger, submitter) {
    const form = component?.querySelector('form');
    const card = component?.closest('.product-details')?.querySelector('[data-fmr-advent-pair]');
    if (!card || bypassForms.has(form)) return;
    const dialog = card.querySelector('[data-fmr-advent-upsell]');
    const enabled = isUpsellEnabled(card) && card.dataset.offerAvailable === 'true' && dialog?.showModal;
    if (!getState(card).busy && !activeOffer && !enabled) return;
    const context = getContext(card);
    if (!context) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!getState(card).busy && !activeOffer) void openOffer(context, trigger, submitter);
  }

  // Window capture runs before the theme's document capture handlers, preventing early ATC animations too.
  window.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (!button || button.disabled) return;
    const component = button.closest('#advent-shop product-form-component');
    if (component && button.matches('add-to-cart-component button')) {
      intercept(event, component, button);
      return;
    }
    const sticky = button.closest('sticky-add-to-cart');
    if (!sticky || !button.matches('[ref="addToCartButton"]')) return;
    const main = sticky.closest('.shopify-section')?.querySelector('#advent-shop product-form-component');
    if (main?.dataset.productId === sticky.dataset.productId) intercept(event, main, button);
  }, true);

  window.addEventListener('submit', (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const component = event.target.closest('#advent-shop product-form-component');
    if (component) intercept(event, component, null, event.submitter);
  }, true);

  async function addMatchingSet(card, dialog = null) {
    const state = getState(card);
    if (state.busy || card.dataset.offerAvailable !== 'true') return;
    const context = getContext(card);
    if (!context) {
      showError(card, 'Please choose an available Advent Calendar and a valid quantity.');
      return;
    }
    const offer = dialog ? activeOffer : null;
    if (dialog && (!offer || offer.checking)) return;
    state.busy = true;
    if (offer) offer.adding = true;
    showError(card, '');
    const button = dialog?.querySelector('[data-fmr-upsell-accept]') ?? card.querySelector('[data-fmr-advent-pair-add]');
    const label = button.textContent;
    const controls = dialog?.querySelectorAll('[data-fmr-upsell-close], [data-fmr-upsell-decline]') ?? [];
    controls.forEach((control) => { control.disabled = true; });
    button.disabled = true;
    button.textContent = 'Adding both…';
    card.querySelector('[data-fmr-advent-pair-add]').disabled = true;

    try {
      const availability = await checkAvailability(card);
      if (!availability.available) {
        if (offer) closeOffer(offer);
        showError(card, `${availability.label}. Please use Add to cart to order your Advent Calendar.`);
        return;
      }
      const items = [{ id: context.bundleId, quantity: 1 }];
      if (context.quantity > 1) items.push({ id: context.variantId, quantity: context.quantity - 1 });
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((component) => component.dataset.sectionId))].slice(0, 5);
      const cart = await window.FmrCart.addMultiple(items, null, {
        sections,
        sections_url: window.location.pathname,
      });
      if (cart) {
        if (offer && activeOffer === offer) closeOffer(offer);
      } else {
        const latest = await checkAvailability(card);
        const message = latest.available
          ? 'We couldn’t confirm your cart update. Please check your cart before trying again.'
          : `${latest.label}. You can still order the Advent Calendar.`;
        showError(card, message);
        if (offer) {
          if (!latest.available) closeOffer(offer);
          else dialog.querySelector('[data-fmr-upsell-status]').textContent = message;
        }
      }
    } finally {
      state.busy = false;
      if (offer) offer.adding = false;
      controls.forEach((control) => { control.disabled = false; });
      const available = card.dataset.offerAvailable === 'true';
      if (dialog || available) button.textContent = label;
      button.disabled = !available;
      const inline = card.querySelector('[data-fmr-advent-pair-add]');
      inline.disabled = !available;
      if (available) inline.textContent = inline.dataset.buttonLabel;
    }
  }

  // Close before the theme opens its cart drawer; keep only one modal active.
  window.addEventListener('cart:update', (event) => {
    if (activeOffer?.adding && event.detail?.data?.source === 'fmr-cart' && !event.detail.data.didError) {
      closeOffer(activeOffer);
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-fmr-advent-pair-add], [data-fmr-upsell-accept], [data-fmr-upsell-decline], [data-fmr-upsell-close]');
    if (!button || button.disabled) return;
    const card = button.closest('[data-fmr-advent-pair]');
    if (button.matches('[data-fmr-advent-pair-add]')) void addMatchingSet(card);
    else if (button.matches('[data-fmr-upsell-accept]')) void addMatchingSet(card, activeOffer?.dialog);
    else if (activeOffer) {
      const offer = activeOffer;
      closeOffer(offer);
      if (button.matches('[data-fmr-upsell-decline]')) continueCalendar(offer);
    }
  });

  function init(container = document) {
    const cards = container.matches?.('[data-fmr-advent-pair]') ? [container] : container.querySelectorAll('[data-fmr-advent-pair]');
    cards.forEach((card) => {
      if (states.has(card)) return;
      getState(card);
      const button = card.querySelector('[data-fmr-advent-pair-add]');
      if (window.FmrCart?.addMultiple) button.hidden = false;
      const dialog = card.querySelector('[data-fmr-advent-upsell]');
      if (!dialog) return;
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        if (activeOffer && !activeOffer.adding) closeOffer(activeOffer);
      });
      dialog.addEventListener('close', () => {
        if (dialog.open) return;
        if (activeOffer?.dialog === dialog) activeOffer = null;
        if (!activeOffer) document.documentElement.classList.remove('fmr-advent-upsell-open');
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', () => {
    if (activeOffer && !activeOffer.card.isConnected) closeOffer(activeOffer);
  });
})();
